import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Stack,
  Switch,
  Text,
  VStack
} from '@chakra-ui/react'
import type { InsuranceParamsDto, Offer, OfferLeasing, Variant } from 'BrokerTsLib/src/dtos'
import { Product, Risk } from 'BrokerTsLib/src/enums'
import { TryParseInt } from 'BrokerTsLib/src/Utils/parseInt'
import { useEffect, useState } from 'react'

import { useOffersAddonContext } from '@/context/OffersAddonProvider'
import { usePrintContext } from '@/context/PrintContext'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { useInsuranceParams } from '../context/InsuranceParamsProvider'
import { useOffersVehicleContext } from '../context/OffersVehicleProvider'
import { useGetRisks, useGetRiskVariants } from '../hooks'
import { useApp } from '../hooks/useApp'

interface FilterSectionGroupProps {
  name: string
  children: React.ReactNode
}

export interface IFilterDS {
  insurers?: { insurerId: number, insurerName: string }[],
  // riskId: riskVariantId
  [n: number]: number[]

  fixed?: boolean,
  float?: boolean,

  fixedComplicity?: boolean,
  percentageComplicity?: boolean

  addons?: {
    // riskId: insurerProductId
    [n: number]: number[]
  }
}

const FilterSectionGroup = ({ name, children }: FilterSectionGroupProps) => {
  return (
    <AccordionItem borderColor={'gray.100'} _first={{ borderTopColor: 'transparent' }}
      sx={{
        gap: { base: 2, md: 4 },
      }}
    >
      <AccordionButton justifyContent={'space-between'} py={2.5} px={2.5}>
        <Text fontSize={'sm'} fontWeight={'semibold'}>{name}</Text>
        <AccordionIcon color={'gray.400'} />
      </AccordionButton>

      <AccordionPanel sx={{ display: 'flex', flexDir: 'column', gap: 2.5, p: 2.5 }} >
        {children}
      </AccordionPanel>
    </AccordionItem >
  )
}

interface CustomVStackProps {
  children: React.ReactNode
}

const CustomVStack = ({ children, ...rest }: CustomVStackProps) => {
  return (
    <VStack align="start" sx={{ gap: { base: 2, md: 3 } }} {...rest}>
      {children}
    </VStack>
  )
}

interface QuotesFilterProps {
  showFilter: boolean
  onCloseClick?: () => void
  onIncludeFiltered: (exclusive: boolean) => void
}

const setLeasingFilter = (ds: IFilterDS, o: OfferLeasing) => {
  // filter na fixed price
  if (o.fixedPrice)
    ds.fixed = true

  if (!o.fixedPrice)
    ds.float = true
}

export const QuotesFilter = ({ showFilter = false, onCloseClick, onIncludeFiltered }: QuotesFilterProps) => {
  const { product } = useApp()
  const { printMode } = usePrintContext()

  const { insuranceParams: ip } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsDto
  const {
    offers,
    updateOffer,
    showAll,
    showWithErrors,
    filtered,
    priceRange,
    priceRangeFilter,
    handleChangeFilterFixed,
    handleChangeFilterFixedComplicity,
    handleChangeFilterInsurer,
    handleChangeFilterRiskVariant,
    handleChangeFilterRisk,
    handleChangeFilterPriceRange,
    setShowWithErrors,
    setShowAll,
    filter,
    resetFilter,
    filterAplied
  } = useOffersVehicleContext()

  const { offers: addonOffers, updateOffer: updateAddonOffer } = useOffersAddonContext()
  const activeAddonOffers = addonOffers.filter(x => x.isActive && x.errors.filter(y => !y.softError).length == 0 && x.price > 0) ?? []

  const [dataSource, setDataSource] = useState<IFilterDS>({})

  // nastaveni datasource pro filtraci
  useEffect(() => {
    const ofrs = !showAll ? offers.filter(x => x.included) : offers
    const ds = getQuotesFilterDS(ofrs, insuranceParams?.baseParams.variants, product == Product.VehicleLeasingInsurance ? setLeasingFilter as any : undefined)
    setDataSource(ds)
  }, [offers, showAll, insuranceParams?.baseParams?.variants])

  const { data: dataRisks } = useGetRisks()
  const { data: dataRisksVariants } = useGetRiskVariants(undefined)

  const handleClose = () => {
    if (onCloseClick) {
      onCloseClick()
    }
  }

  const anyErrorQuote = offers.filter(x => x.errors.filter(y => !y.softError).length > 0 || x.price == 0).length > 0
  const anyHidden = offers.filter(x => !x.included).length > 0

  const toPrintCount = [...offers, ...activeAddonOffers].filter(x => x.print && x.errors.filter(y => !y.softError).length == 0 && x.price > 0).map(x => x.offerId).length

  return (
    <Grid
      sx={{
        display: {
          base: showFilter ? 'grid' : 'none',
          md: 'grid',
        },
        alignContent: 'start',
        position: { base: 'fixed', md: 'static' },
        bottom: { base: 4, md: 'auto' },
        left: { base: 4, md: 'auto' },
        width: { base: 'calc(100% - 32px)', md: 'auto' },
        zIndex: 10
      }}
    >
      <CloseButton
        sx={{
          display: {
            base: 'block',
            md: 'none',
          },
          position: {
            base: 'absolute',
            md: 'static',
          },
          top: 4,
          right: 4,
          zIndex: 11,
        }}
        onClick={handleClose}
      />

      <Box
        sx={{
          display: {
            base: 'block',
            md: 'none',
          },
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          bg: 'blackAlpha.500',
          zIndex: -1,
        }}
        onClick={handleClose}
      />

      <Accordion allowMultiple defaultIndex={[0]}
        sx={{
          gap: 6,
          position: 'relative',
          maxHeight: { base: 'calc(100vh - 72px)', md: 'auto' },
          overflowY: { base: 'auto', md: 'hidden' },
          p: { base: 4, md: 0 },
          bg: {
            base: 'white',
            md: 'transparent',
          },
          borderRadius: {
            base: 'md',
            md: 'none',
          },
          boxShadow: {
            base: 'xl',
            md: 'none',
          },
          zIndex: 10,
          pr: { base: 4, md: '3px' },
        }}
      >

        {dataSource.insurers &&
          <FilterSectionGroup name='Pojišťovny'>
            <CustomVStack>
              {dataSource.insurers.map(insurer => {
                return (
                  <Checkbox
                    key={insurer.insurerId}
                    size='sm'
                    value={insurer.insurerId.toString()}
                    isChecked={filter.displayOnlyInsurers.some(x => x == insurer.insurerId)}
                    onChange={(e) => handleChangeFilterInsurer(insurer.insurerId, e.target.checked)}
                  >
                    {insurer.insurerName}
                  </Checkbox>
                )
              })}
            </CustomVStack>
          </FilterSectionGroup>
        }

        {dataRisks && dataRisks.data && dataRisksVariants && dataRisksVariants.data && Object.keys(dataSource).map(riskId => {
          if (!TryParseInt(riskId)) return

          const risk = dataRisks.data.find(x => x.id == parseInt(riskId))!
          if (risk?.value == Risk.SKLA.toString()) return

          const riskVariants = dataSource[risk.id]

          return (
            <FilterSectionGroup name={risk?.name} key={riskId}>
              <CustomVStack>
                {riskId == Risk.SPOL.toString() && dataSource.fixedComplicity && dataSource.percentageComplicity &&
                  <>
                    <Checkbox
                      size='sm'
                      isChecked={filter.displayOnlyFixedComplicity}
                      onChange={(e) => handleChangeFilterFixedComplicity(e.target.checked, undefined)}
                    >
                      Fix
                    </Checkbox>

                    <Checkbox
                      size='sm'
                      isChecked={filter.displayOnlyPercentageComplicity}
                      onChange={(e) => handleChangeFilterFixedComplicity(undefined, e.target.checked)}
                    >
                      %
                    </Checkbox>
                    <Box></Box>
                  </>
                }

                {riskVariants.map(riskVariantId => {
                  const name = dataRisksVariants.data.find(x => x.id == riskVariantId)?.name?.replace('Spoluúčast', '')
                  if (!name) return null

                  return (
                    <Checkbox
                      key={riskVariantId}
                      size='sm'
                      value={riskVariantId}
                      isChecked={filter.displayOnlyRiskVariants[risk.id]?.some(x => x == riskVariantId) ?? false}
                      onChange={(e) => handleChangeFilterRiskVariant(risk.id, riskVariantId, e.target.checked)}
                    >
                      {name}
                    </Checkbox>
                  )
                })}

              </CustomVStack>
            </FilterSectionGroup>
          )
        })}

        {product == Product.VehicleLeasingInsurance && dataSource?.fixed && dataSource?.float && filtered &&
          <FilterSectionGroup name='Typ sazby'>
            <CustomVStack>
              <Checkbox
                size='sm'
                isChecked={filter.displayOnlyFloat}
                onChange={(e) => handleChangeFilterFixed(undefined, e.target.checked)}
              >
                float
              </Checkbox>

              <Checkbox
                size='sm'
                isChecked={filter.displayOnlyFixed}
                onChange={(e) => handleChangeFilterFixed(e.target.checked, undefined)}
              >
                fix
              </Checkbox>
            </CustomVStack>
          </FilterSectionGroup>
        }

        {dataRisks?.data && dataSource.addons &&
          <FilterSectionGroup name='Doplňková připojištění'>
            {
              Object.keys(dataSource.addons).map(riskId => {
                if (!TryParseInt(riskId)) return

                const risk = dataRisks.data.find(x => x.id == parseInt(riskId))!

                return (
                  <Checkbox
                    key={riskId}
                    size='sm'
                    value={riskId.toString()}
                    isChecked={filter.displayOnlyRisks.some(x => x == risk.id)}
                    onChange={(e) => handleChangeFilterRisk(risk.id, e.target.checked)}
                  >
                    {risk?.name}
                  </Checkbox>
                )
              })
            }
          </FilterSectionGroup>
        }
      </Accordion>

      <Flex flexDir={'column'} gap={2.5} pl={2.5} pr={2} pt={2.5}>
        <Text fontSize={'sm'} fontWeight={'semibold'}>Ostatní</Text>

        {priceRange[0] !== priceRange[1] &&
          <Box sx={{ pl: 3, pr: 6, mb: 6, mt: 6 }}>
            <RangeSlider
              aria-label={['min', 'max']}
              step={100}
              min={priceRange.at(0)}
              max={priceRange.at(1)}
              value={priceRangeFilter}
              onChange={(range) => handleChangeFilterPriceRange(range[0], range[1])}
              title={`min. ${priceFormat(priceRange[0], null, 'N/A')}, max. ${priceFormat(priceRange[1], null, 'N/A')}`}
            >
              <RangeSliderTrack>
                <RangeSliderFilledTrack />
              </RangeSliderTrack>
              <RangeSliderThumb index={0}>
                <Text fontSize={'sm'} fontWeight={'bold'} sx={{ position: 'absolute', left: -1, bottom: 6 }}>{priceFormat(priceRangeFilter[0], null, 'N/A')}</Text>
              </RangeSliderThumb>
              <RangeSliderThumb index={1}>
                <Text fontSize={'sm'} fontWeight={'bold'} sx={{ position: 'absolute', right: -1, top: 6 }}>{priceFormat(priceRangeFilter[1], null, 'N/A')}</Text>
              </RangeSliderThumb>
            </RangeSlider>
          </Box>
        }

        {anyErrorQuote &&
          !printMode && // v print modu se chybove nezobrazuji
          <FormControl display='flex' alignItems='center'>
            <Switch id="showWithErrors"
              size='sm'
              isChecked={showWithErrors}
              onChange={() => setShowWithErrors(!showWithErrors)} />
            <FormLabel variant={'switch'} fontSize={'sm'} fontWeight={'medium'} htmlFor='showWithErrors' cursor='pointer'>Zobrazit chybné kalkulace</FormLabel>
          </FormControl>
        }

        {anyHidden &&
          <FormControl display='flex' alignItems='center'>
            <Switch id="showAll"
              size='sm'
              isChecked={showAll}
              onChange={() => setShowAll(!showAll)} />
            <FormLabel variant={'switch'} fontSize={'sm'} fontWeight={'medium'} htmlFor='showAll' cursor='pointer'>Zobrazit nevybrané</FormLabel>
          </FormControl>
        }
      </Flex>

      <Stack id='filterButtons' mx={4} gap={4} mt={6}>
        {filterAplied && <Button variant='link' colorScheme='gray' onClick={resetFilter} _focus={{ boxShadow: 'none' }}>Resetovat filtr</Button>}

        {printMode &&
          <Stack id='printFilterButtons'>
            <Button px={0} colorScheme="primary"
              onClick={() => {
                filtered.forEach(x => {
                  x.print = true
                  updateOffer(x)
                })

                activeAddonOffers.forEach(x => {
                  x.print = true
                  updateAddonOffer(x)
                })
              }}
              isDisabled={[...filtered, ...activeAddonOffers].filter(x => !x.print).length == 0}>
              Vybrat zobrazené k tisku ({[...filtered, ...activeAddonOffers].filter(x => !x.print).length})
            </Button>

            <Button
              colorScheme="primary"
              onClick={() => {
                // nejdriv vsechny na false
                offers.forEach(o => {
                  o.print = false
                  updateOffer(o)
                })

                addonOffers.forEach(o => {
                  o.print = false
                  updateAddonOffer(o)
                })

                // nasledne vybrane na true
                filtered.forEach(x => {
                  x.print = true
                  updateOffer(x)
                })

                activeAddonOffers.forEach(x => {
                  x.print = true
                  updateAddonOffer(x)
                })
              }}
              isDisabled={[...filtered, ...activeAddonOffers].filter(x => x.print).length === toPrintCount && toPrintCount > 0}
            >
              Tisk pouze zobrazených ({[...filtered, ...activeAddonOffers].length})
            </Button>

            {toPrintCount > 0 &&
              <Button colorScheme="gray" onClick={() => {
                filtered.forEach(x => {
                  x.print = false
                  updateOffer(x)
                })

                activeAddonOffers.forEach(x => {
                  x.print = false
                  updateAddonOffer(x)
                })
              }}>
                Odebrat zobrazené
              </Button>
            }

            {[...offers, ...activeAddonOffers].filter(x => x.print).length > 0 &&
              <Button
                colorScheme="gray"
                onClick={() => {
                  offers.forEach(o => {
                    o.print = false
                    updateOffer(o)
                  })

                  activeAddonOffers.forEach(x => {
                    x.print = false
                    updateAddonOffer(x)
                  })
                }}
              >
                Odebrat vše
              </Button>
            }
          </Stack>
        }

      </Stack>
    </Grid >
  )
}

export const mainRisks = [Risk.POV, Risk.HAV, Risk.SPOL]
export const getQuotesFilterDS = (offers: Offer[], variants?: Variant[], updateDsCallback?: (ds: IFilterDS, offer: Offer, variants?: Variant[]) => void) => {

  const ds: IFilterDS = {}

  offers.forEach(offer => {
    // filtr na pojistovny
    if (!ds.insurers)
      ds.insurers = []

    if (ds.insurers.findIndex(i => i.insurerId == offer.insurerId) < 0)
      ds.insurers.push({ insurerId: offer.insurerId, insurerName: offer.insurerName })

    const spolPart = offer.parts.find(x => x.riskId == Risk.SPOL)
    if (spolPart) {
      if (spolPart.complicityFix)
        ds.fixedComplicity = true

      if (spolPart.complicityPercentage)
        ds.percentageComplicity = true
    }

    // filtr na hlavni rizika
    if (offer.parts && !!variants) {
      // rizika beru na zaklade varianty ze ktere byla vygenerovana dana offera

      const variantParts = [
        ...variants
          ?.find(x => x.id == offer.insuranceVariantId)
          ?.variantParts
          ?.filter(x => mainRisks.includes(x.risk))
        ?? []
      ]

      // vsechny riskVarianty dane offery se pridaji do data source filtru
      variantParts.forEach(part => {
        if (!part.riskVariant || !part.risk) return

        if (!(part.risk in ds))
          ds[part.risk] = []

        if (ds[part.risk].indexOf(part.riskVariant) < 0)
          ds[part.risk].push(part.riskVariant)
      })
    }

    offer.parts
      ?.filter(x => !mainRisks.includes(x.riskId))
      ?.forEach(x => {
        // asistenci ve filtru nechceme - kazda offera ma asistenci
        if (x.riskId == Risk.ASISTENCE)
          return

        if (!ds.addons)
          ds.addons = {}

        if (!(x.riskId in ds.addons))
          ds.addons[x.riskId] = []

        if (ds.addons[x.riskId].indexOf(x.productId ?? 0) < 0)
          ds.addons[x.riskId].push(x.productId ?? 0)
      })

    // napr product specific ds
    if (updateDsCallback)
      updateDsCallback(ds, offer, variants)
  })

  return ds
}